import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, IconButton, Link, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import {
    EmissionRecord,
    EmissionRecordsPage,
    EmissionRecordType,
    EmissionsConfiguration,
    EmissionsTimelineResult,
    OutboundMessagingConfiguration,
} from "models/Emissions";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ServiceError } from "services/BaseService";
import EmissionsService from "services/EmissionsService";
import theme from "Theme/AppTheme";
import EmissionRecordDetailPanel from "./EmissionRecordDetailPanel";
import { RecordTypeChip } from "./EmissionsChips";

type Option = { id: string; label: string };

const typeOptions: (Option & { recordType?: EmissionRecordType })[] = [
    { id: "all", label: "All record types" },
    { id: "receipts", label: "Receipts", recordType: "Receipt" },
    { id: "failures", label: "Failures", recordType: "Failure" },
    {
        id: "rules",
        label: "Rule Filter Evaluations",
        recordType: "RulesFilterEvaluation",
    },
];

// 90 days is the endpoint's ceiling for failures and rule filter evaluations, so there is no
// all-time option: it would return receipts from any date beside a silently truncated history.
const rangeOptions: (Option & { days: number })[] = [
    { id: "7d", label: "Last 7 days", days: 7 },
    { id: "30d", label: "Last 30 days", days: 30 },
    { id: "90d", label: "Last 90 days", days: 90 },
];

const ALL_CONFIGURATIONS: Option = { id: "all", label: "All configurations" };
const DEFAULT_RANGE = "7d";
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const GUID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const PARAMS = {
    conversationId: "conversationId",
    type: "type",
    config: "config",
    range: "range",
    page: "page",
    pageSize: "pageSize",
};

const SELECTED_ROW_CLASS = "emission-record-selected";

const formatTimestamp = (value: string) =>
    new Date(value).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
    });

const formatReason = (reason: unknown) =>
    typeof reason === "string" ? reason : JSON.stringify(reason);

const getRecordKey = (record: EmissionRecord) =>
    `${record.recordType}-${record.recordId}`;

export const getRecordDetail = (record: EmissionRecord) => {
    switch (record.recordType) {
        case "Receipt":
            return record.receipt?.emissionReason
                ? `Emission reason · ${formatReason(
                      record.receipt.emissionReason,
                  )}`
                : "Delivered";
        case "Failure":
            return record.failure?.errorMessage ?? "Failed";
        case "RulesFilterEvaluation":
            return record.rulesFilterEvaluation?.passed
                ? "Passed"
                : "Not passed";
    }
};

export const getRecordAttempts = (record: EmissionRecord) => {
    if (
        record.recordType === "RulesFilterEvaluation" ||
        record.attempts === null
    )
        return "–";
    return record.recordType === "Failure"
        ? `${record.attempts} total`
        : String(record.attempts);
};

export const timelineToRecords = (
    timeline: EmissionsTimelineResult,
): EmissionRecord[] => [
    ...timeline.configurableEmissionsReceipts.map(
        (receipt): EmissionRecord => ({
            recordId: receipt.id,
            recordType: "Receipt",
            timestamp: receipt.emittedOn,
            audioMetadataId: receipt.audioMetadataId,
            configurationId: receipt.outboundMessagingConfigurationId,
            attempts: receipt.attempt,
            receipt,
        }),
    ),
    ...timeline.outboundEmissionsFailures.map(
        (failure): EmissionRecord => ({
            recordId: failure.rowKey,
            recordType: "Failure",
            timestamp: failure.failedAt,
            audioMetadataId: failure.audioMetadataId,
            configurationId: failure.outboundConfigurationId,
            attempts: failure.totalAttempts,
            failure,
        }),
    ),
    ...timeline.rulesFilterEvaluations.map(
        (evaluation): EmissionRecord => ({
            recordId: evaluation.rowKey,
            recordType: "RulesFilterEvaluation",
            timestamp: evaluation.evaluatedAt,
            audioMetadataId: evaluation.audioMetadataId,
            configurationId: evaluation.configurationId,
            attempts: null,
            rulesFilterEvaluation: evaluation,
        }),
    ),
];

type Props = {
    service: EmissionsService;
    endpoints: OutboundMessagingConfiguration[];
    bodies: EmissionsConfiguration[];
};

const EmissionRecordsTab: React.FC<Props> = ({
    service,
    endpoints,
    bodies,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const conversationId = searchParams.get(PARAMS.conversationId) ?? "";
    const isConversationMode = GUID_PATTERN.test(conversationId);
    const typeOption =
        typeOptions.find(
            (option) => option.id === searchParams.get(PARAMS.type),
        ) ?? typeOptions[0];
    const rangeOption =
        rangeOptions.find(
            (option) =>
                option.id === (searchParams.get(PARAMS.range) ?? DEFAULT_RANGE),
        ) ?? rangeOptions[0];
    const configurationId = searchParams.get(PARAMS.config) ?? "";
    // Number("x") is NaN, which compares false everywhere and would leave the grid permanently
    // empty with nothing to click back to.
    const requestedPage = Number(searchParams.get(PARAMS.page) ?? 1);
    const page = Number.isFinite(requestedPage)
        ? Math.max(requestedPage - 1, 0)
        : 0;
    const pageSize =
        PAGE_SIZE_OPTIONS.find(
            (size) => size === Number(searchParams.get(PARAMS.pageSize)),
        ) ?? PAGE_SIZE_OPTIONS[0];

    const [searchText, setSearchText] = useState(conversationId);
    const [listResult, setListResult] = useState<EmissionRecordsPage>({
        items: [],
        hasNextPage: false,
    });
    const [conversationRecords, setConversationRecords] = useState<
        EmissionRecord[]
    >([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isNotFound, setIsNotFound] = useState(false);
    // The window is anchored when the range is picked or the list is reloaded, never per page.
    // The service caches a cursor per filter set, so a boundary that moved with every page would
    // make each page a new set and re-walk the list from the start.
    const [listEpoch, setListEpoch] = useState(() => Date.now());
    const from = new Date(
        listEpoch - rangeOption.days * 86400000,
    ).toISOString();

    const [selectedKey, setSelectedKey] = useState<string>();

    useEffect(() => setSearchText(conversationId), [conversationId]);
    useEffect(() => setSelectedKey(undefined), [conversationId]);

    const configurationNames = useMemo(() => {
        const names = new Map<string, string>();
        endpoints.forEach((endpoint) =>
            names.set(endpoint.id.toLowerCase(), endpoint.name),
        );
        bodies.forEach((body) => names.set(body.id.toLowerCase(), body.name));
        return names;
    }, [endpoints, bodies]);
    const getConfigurationName = (id: string) =>
        configurationNames.get(id.toLowerCase()) ?? id;

    // Only active configurations are offered as filters, but configurationNames above stays
    // unfiltered so records written by a since-disabled configuration still resolve to a name.
    const configurationOptions = useMemo<Option[]>(
        () => [
            ALL_CONFIGURATIONS,
            ...endpoints
                .filter((endpoint) => endpoint.isActive)
                .map((endpoint) => ({
                    id: endpoint.id,
                    label: endpoint.name,
                })),
            ...bodies
                .filter((body) => body.isActive)
                .map((body) => ({ id: body.id, label: body.name })),
        ],
        [endpoints, bodies],
    );
    // A configuration disabled after it was filtered on, or one named in a link, is not in the
    // list above. Showing "All configurations" while still filtering by it would describe the
    // grid wrongly, so the selection is named when it is known and dropped when it is not.
    const selectedConfiguration = configurationId
        ? (configurationOptions.find(
              (option) =>
                  option.id.toLowerCase() === configurationId.toLowerCase(),
          ) ??
          (configurationNames.has(configurationId.toLowerCase())
              ? {
                    id: configurationId,
                    label: getConfigurationName(configurationId),
                }
              : undefined))
        : undefined;
    const configurationOption = selectedConfiguration ?? ALL_CONFIGURATIONS;
    const appliedConfigurationId = selectedConfiguration ? configurationId : "";

    const updateParams = (changes: Record<string, string | undefined>) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) =>
            value ? next.set(key, value) : next.delete(key),
        );
        if (!(PARAMS.page in changes)) next.delete(PARAMS.page);
        setSearchParams(next);
    };

    const applySearch = (value: string) => {
        const trimmed = value.trim();
        if (trimmed !== conversationId)
            updateParams({ [PARAMS.conversationId]: trimmed || undefined });
    };

    // The timeline reports when the older rule filter evaluations could not all be read. The
    // records it did return are still shown; only the count stops being a total. The two fetches
    // that can report it are tracked apart so neither leaves a warning behind on the other's
    // results: the org-wide list reads the index and never runs the legacy scan at all.
    const [timelineIncomplete, setTimelineIncomplete] = useState(false);
    const [detailIncomplete, setDetailIncomplete] = useState(false);

    useEffect(() => {
        if (!isConversationMode) return;
        const controller = new AbortController();
        setIsLoading(true);
        setHasError(false);
        setIsNotFound(false);
        setTimelineIncomplete(false);

        service
            .getEmissionsTimeline(conversationId, controller.signal)
            .then((timeline) => {
                if (!controller.signal.aborted) {
                    setConversationRecords(timelineToRecords(timeline));
                    setTimelineIncomplete(
                        Boolean(timeline.legacyRulesFilterEvaluationsIncomplete),
                    );
                }
            })
            .catch((error) => {
                if (controller.signal.aborted) return;
                setConversationRecords([]);
                if (
                    error instanceof ServiceError &&
                    error.response.status === 404
                )
                    setIsNotFound(true);
                else setHasError(true);
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsLoading(false);
            });

        return () => controller.abort();
    }, [service, isConversationMode, conversationId, listEpoch]);

    useEffect(() => {
        if (isConversationMode) return;
        const controller = new AbortController();
        setIsLoading(true);
        setHasError(false);
        setIsNotFound(false);

        service
            .getEmissionRecords(
                {
                    conversationId: conversationId || undefined,
                    recordType: typeOption.recordType,
                    configurationId: appliedConfigurationId || undefined,
                    from,
                    page: page + 1,
                    pageSize,
                    sortDirection: "desc",
                },
                controller.signal,
            )
            .then((loaded) => {
                if (controller.signal.aborted) return;
                // A page beyond the end comes back empty: a deep link to one, or rows that have
                // since gone. Falling back to the first page shows the list instead of a void.
                if (!loaded.items.length && page > 0) {
                    updateParamsRef.current({ [PARAMS.page]: undefined });
                    return;
                }

                setListResult(loaded);
            })
            .catch(() => {
                if (!controller.signal.aborted) setHasError(true);
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsLoading(false);
            });

        return () => controller.abort();
    }, [
        service,
        isConversationMode,
        conversationId,
        typeOption.recordType,
        appliedConfigurationId,
        from,
        page,
        pageSize,
    ]);

    const rows = useMemo(() => {
        if (!isConversationMode) return listResult.items;
        return conversationRecords
            .filter(
                (record) =>
                    (!typeOption.recordType ||
                        record.recordType === typeOption.recordType) &&
                    (!appliedConfigurationId ||
                        record.configurationId.toLowerCase() ===
                            appliedConfigurationId.toLowerCase()),
            )
            .sort(
                (a, b) =>
                    new Date(a.timestamp).getTime() -
                    new Date(b.timestamp).getTime(),
            );
    }, [
        isConversationMode,
        listResult,
        conversationRecords,
        typeOption.recordType,
        appliedConfigurationId,
    ]);

    const selectedRecord = rows.find(
        (record) => getRecordKey(record) === selectedKey,
    );

    // The list carries summaries (no rule diagnostics, truncated errors), so a selected failure or
    // evaluation loads its full record from the conversation's timeline
    const [fullRecord, setFullRecord] = useState<EmissionRecord>();
    useEffect(() => {
        setFullRecord(undefined);
        setDetailIncomplete(false);
        if (isConversationMode || !selectedRecord || selectedRecord.receipt)
            return;
        const controller = new AbortController();
        const rowKey = (
            selectedRecord.failure ?? selectedRecord.rulesFilterEvaluation
        )?.rowKey;

        service
            .getEmissionsTimeline(
                selectedRecord.audioMetadataId,
                controller.signal,
            )
            .then((timeline) => {
                if (controller.signal.aborted) return;
                if (timeline.legacyRulesFilterEvaluationsIncomplete)
                    setDetailIncomplete(true);
                setFullRecord(
                    timelineToRecords(timeline).find(
                        (record) =>
                            record.recordType === selectedRecord.recordType &&
                            (record.failure ?? record.rulesFilterEvaluation)
                                ?.rowKey === rowKey,
                    ),
                );
            })
            .catch(() => undefined); // the summary stays on screen

        return () => controller.abort();
    }, [service, isConversationMode, selectedRecord]);

    // Only the conversation table can itself be partial. A detail refetch reports on one
    // conversation's lookup, so that warning belongs in the panel and leaves the list alone.
    const isPartial = isConversationMode && timelineIncomplete;

    const activeFilterLabels = [
        conversationId,
        typeOption.recordType ? typeOption.label : undefined,
        configurationOption !== ALL_CONFIGURATIONS
            ? configurationOption.label
            : undefined,
        rangeOption.label,
    ].filter(Boolean);

    const filterSummary = isConversationMode
        ? `${rows.length} ${rows.length === 1 ? "record" : "records"}${
              isPartial ? " loaded" : ""
          } · oldest first`
        : !conversationId &&
          !typeOption.recordType &&
          configurationOption === ALL_CONFIGURATIONS
        ? `No filters · all record types, ${rangeOption.label.toLowerCase()}, newest first`
        : `${activeFilterLabels.join(" · ")} · newest first`;

    // A reload starts the list over: new cursors, a fresh window, back to the first page.
    const reloadList = () => {
        service.clearEmissionRecordCursors();
        setListEpoch(Date.now());
        updateParams({ [PARAMS.page]: undefined });
    };

    const updateParamsRef = useRef(updateParams);
    updateParamsRef.current = updateParams;

    const grid = useMemo(() => {
        const store = new AcxDataGridStore("emissionRecords", "EmissionsAdmin");
        store.disableLocalConfig = true;
        store.hideHeader = true;
        store.checkboxSelection = false;
        store.disableRowSelectionOnClick = true;
        store.rowHeightOverride = true;
        store.heightOverride = "560px";
        store.pageSizeOptions = PAGE_SIZE_OPTIONS;
        const toggleSelection = (id: unknown) => {
            const key = String(id);
            setSelectedKey((current) => (current === key ? undefined : key));
        };
        store.onCellClick = (params) => toggleSelection(params.id);
        store.onCellKeyDown = (params, event) => {
            if (event.key !== "Enter") return;
            // The Conversation Id cell holds its own button; let Enter reach it rather than
            // opening the panel on top of it.
            const target = event.target as HTMLElement | null;
            if (target?.closest("a, button, input, textarea, select")) return;
            event.defaultMuiPrevented = true;
            toggleSelection(params.id);
        };
        return store;
    }, []);

    useEffect(() => {
        const columns: IColDef[] = [
            {
                field: "timestamp",
                headerName: "Timestamp",
                flex: 1.25,
                // Floors keep these legible once Configuration shares the width; without them
                // the header clips to "Atte..." and timestamps wrap onto a second line.
                minWidth: 150,
                renderCell: ({ row }) => formatTimestamp(row.timestamp),
            },
            {
                field: "recordType",
                headerName: "Record Type",
                flex: 1.3,
                // The chip ellipsises its own label, so "Rule Filter Eval" needs room to sit
                // whole rather than being cut to "Rule Filter ...".
                minWidth: 150,
                renderCell: ({ row }) => (
                    <RecordTypeChip type={row.recordType} />
                ),
            },
            ...(isConversationMode
                ? []
                : [
                      {
                          field: "audioMetadataId",
                          headerName: "Conversation Id",
                          flex: 1.3,
                          renderCell: ({ row }) => (
                              <Link
                                  component="button"
                                  underline="hover"
                                  onClick={(event) => {
                                      event.stopPropagation();
                                      updateParamsRef.current({
                                          [PARAMS.conversationId]:
                                              row.audioMetadataId,
                                      });
                                  }}
                                  sx={{
                                      fontWeight: 600,
                                      fontSize: 12,
                                      textAlign: "left",
                                      wordBreak: "break-all",
                                  }}
                              >
                                  {row.audioMetadataId}
                              </Link>
                          ),
                      } as IColDef,
                  ]),
            // Configuration stays visible in conversation mode: one conversation can span
            // several endpoints and emission bodies, so it is what distinguishes the rows.
            {
                field: "configurationId",
                headerName: "Configuration",
                flex: 1.55,
                renderCell: ({ row }) =>
                    configurationNames.get(row.configurationId.toLowerCase()) ??
                    row.configurationId,
            },
            {
                field: "attempts",
                headerName: "Attempts",
                flex: 0.75,
                minWidth: 96,
                renderCell: ({ row }) => getRecordAttempts(row),
            },
            {
                field: "detail",
                headerName: "Detail",
                flex: 2.3,
                renderCell: ({ row }) => (
                    <Box
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {getRecordDetail(row)}
                    </Box>
                ),
            },
        ];
        void grid.setColumns(
            columns.map((column) => ({
                ...column,
                sortable: false,
                filterable: false,
            })),
        );
    }, [grid, isConversationMode, configurationNames]);

    useEffect(() => {
        grid.setRows(
            rows.map((record) => ({ ...record, id: getRecordKey(record) })),
        );
    }, [grid, rows]);

    useEffect(() => {
        grid.setLoading(isLoading);
    }, [grid, isLoading]);

    useEffect(() => {
        grid.getRowClassName = ({ id }) =>
            id === selectedKey ? SELECTED_ROW_CLASS : "";
    }, [grid, selectedKey]);

    useEffect(() => {
        grid.pagination = !isConversationMode;
        grid.paginationMode = isConversationMode ? "client" : "server";
        // Keep the total unknown until the final page. A controlled rowCount of -1 overrides
        // the grid's inferred final count, leaving Next enabled even when hasNextPage is false.
        grid.rowCount = isConversationMode
            ? undefined
            : listResult.hasNextPage
              ? -1
              : page * pageSize + listResult.items.length;
        // The source cannot count its rows, so the usual "1-25 of more than 25" says nothing the
        // page size selector and the arrows do not already say. The page number does.
        grid.localeText = {
            MuiTablePagination: {
                labelDisplayedRows: ({ page: current }) =>
                    `Page ${current + 1}`,
            },
            ...(isPartial ? { footerTotalRows: "Records loaded:" } : {}),
        };
        grid.paginationMeta = isConversationMode
            ? undefined
            : { hasNextPage: listResult.hasNextPage };
        grid.paginationModel = { page, pageSize };
        grid.onPaginationModelChange = (model) => {
            const pageSizeChanged = model.pageSize !== pageSize;
            updateParamsRef.current({
                [PARAMS.page]:
                    pageSizeChanged || model.page === 0
                        ? undefined
                        : String(model.page + 1),
                [PARAMS.pageSize]:
                    model.pageSize === PAGE_SIZE_OPTIONS[0]
                        ? undefined
                        : String(model.pageSize),
            });
        };
    }, [
        grid,
        isConversationMode,
        isPartial,
        listResult.items.length,
        listResult.hasNextPage,
        page,
        pageSize,
    ]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <AcxMainTextField
                        id="emission-records-conversation-search"
                        placeholderText="Search Conversation Id…"
                        value={searchText}
                        onChange={(evt) => setSearchText(evt.target.value)}
                        onBlur={(value) => applySearch(String(value ?? ""))}
                        blurOnEnterPress
                        fullWidth
                        endAdornment={
                            searchText ? (
                                <IconButton
                                    aria-label="Clear conversation filter"
                                    size="small"
                                    onClick={() => {
                                        setSearchText("");
                                        applySearch("");
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            ) : undefined
                        }
                    />
                </Box>
                <Box sx={{ width: 210 }}>
                    <AcxSelectSingle
                        id="emission-records-type"
                        options={typeOptions}
                        valueField="id"
                        labelField="label"
                        defaultValue={typeOption}
                        onChange={(option) =>
                            updateParams({
                                [PARAMS.type]:
                                    option.id === "all" ? undefined : option.id,
                            })
                        }
                        isSearchable={false}
                        fullWidth
                    />
                </Box>
                <Box sx={{ width: 220 }}>
                    <AcxSelectSingle
                        id="emission-records-configuration"
                        options={configurationOptions}
                        valueField="id"
                        labelField="label"
                        defaultValue={configurationOption}
                        onChange={(option) =>
                            updateParams({
                                [PARAMS.config]:
                                    option.id === ALL_CONFIGURATIONS.id
                                        ? undefined
                                        : option.id,
                            })
                        }
                        isSearchable
                        fullWidth
                    />
                </Box>
                {!isConversationMode && (
                    <Box sx={{ width: 150 }}>
                        <AcxSelectSingle
                            id="emission-records-range"
                            options={rangeOptions}
                            valueField="id"
                            labelField="label"
                            defaultValue={rangeOption}
                            onChange={(option) => {
                                setListEpoch(Date.now());
                                updateParams({
                                    [PARAMS.range]:
                                        option.id === DEFAULT_RANGE
                                            ? undefined
                                            : option.id,
                                });
                            }}
                            isSearchable={false}
                            fullWidth
                        />
                    </Box>
                )}
            </Box>

            <Typography
                sx={{ fontSize: "12px", color: theme.palette.gray[400] }}
            >
                {filterSummary}
            </Typography>

            {!isConversationMode && (
                <Typography
                    sx={{ fontSize: "12px", color: theme.palette.gray[400] }}
                >
                    Failures and rule filter evaluations reach back 90 days at
                    most, and only as far as indexing has been running.
                </Typography>
            )}

            {isPartial && (
                <Alert severity="warning">
                    Some older rule filter evaluations couldn’t be loaded.
                    Results may be incomplete.
                </Alert>
            )}

            {hasError ? (
                <Alert
                    severity="error"
                    action={
                        <AcxButton onClick={reloadList}>Retry</AcxButton>
                    }
                >
                    Couldn’t load emission records.
                </Alert>
            ) : (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: selectedRecord
                            ? "minmax(0,1fr) 380px"
                            : "minmax(0,1fr)",
                        gap: "20px",
                    }}
                >
                    <Box
                        sx={{
                            minWidth: 0,
                            "& .MuiDataGrid-row": { cursor: "pointer" },
                            [`& .MuiDataGrid-row.${SELECTED_ROW_CLASS}, & .MuiDataGrid-row.${SELECTED_ROW_CLASS}:hover`]:
                                {
                                    backgroundColor: theme.palette.primary[50],
                                    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
                                },
                        }}
                    >
                        {isNotFound && (
                            <Alert severity="info" sx={{ marginBottom: 1 }}>
                                Conversation not found.
                            </Alert>
                        )}
                        <AcxDataGrid dataGridStore={grid} disableColumnResize />
                    </Box>
                    {selectedRecord && (
                        <EmissionRecordDetailPanel
                            record={fullRecord ?? selectedRecord}
                            configurationName={getConfigurationName(
                                selectedRecord.configurationId,
                            )}
                            incomplete={detailIncomplete}
                            onClose={() => setSelectedKey(undefined)}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default EmissionRecordsTab;
